import { groupConsecutiveDates, type DateGroup } from "../date-utils";
import type { Slot } from "./types";

/**
 * Shape minimale attendue d'un DatePoll pour la normalisation.
 * Aligné sur DatePollData (packages/core/src/services/PollService.ts)
 * et sur les settings legacy utilisés par useVoting.
 */
export interface NormalizablePoll {
  settings?: {
    selectedDates?: string[];
    timeSlotsByDate?: Record<
      string,
      Array<{
        hour: number;
        minute: number;
        enabled?: boolean;
        duration?: number;
      }>
    >;
  };
  dateGroups?: DateGroup[];
  selectedDates?: string[];
  timeSlotsByDate?: Record<
    string,
    Array<{
      hour: number;
      minute: number;
      enabled?: boolean;
      duration?: number;
    }>
  >;
}

/**
 * Produit la liste canonique des Slots pour un poll, peu importe la forme
 * legacy dont proviennent les données.
 *
 * Règles :
 * - `dateGroups` explicites (fournis par l'IA) ont priorité
 * - sinon, on groupe les dates consécutives via groupConsecutiveDates
 * - chaque group → 1 Slot avec id `option-${index}` (cohérent avec useVoting)
 * - time slots : uniquement sur groupes mono-date (cf. useVoting ligne 117-120)
 */
export function normalizePollSlots(poll: NormalizablePoll): Slot[] {
  const selectedDates =
    poll.settings?.selectedDates ?? poll.selectedDates ?? [];
  const timeSlotsByDate =
    poll.settings?.timeSlotsByDate ?? poll.timeSlotsByDate ?? {};

  if (selectedDates.length === 0) return [];

  const groups: DateGroup[] =
    poll.dateGroups && poll.dateGroups.length > 0
      ? poll.dateGroups
      : groupConsecutiveDates(selectedDates);

  return groups.map((group, index) => {
    const primaryDate = group.dates[0];
    const isMultiDate = group.dates.length > 1;

    const rawTimeSlots = isMultiDate
      ? null
      : (timeSlotsByDate[primaryDate] ?? null);
    const timeSlots = rawTimeSlots
      ? rawTimeSlots
          .filter((ts) => ts.enabled !== false)
          .map((ts) => ({
            hour: ts.hour,
            minute: ts.minute,
            duration: ts.duration,
          }))
      : null;

    return {
      id: `option-${index}`,
      index,
      primaryDate,
      dates: [...group.dates],
      groupLabel: isMultiDate ? group.label : undefined,
      timeSlots,
    };
  });
}
